import { RefObject, useEffect, useRef, useState } from 'react';
import { RelBox } from '../../../api/ai.api.types';
import { grayscaleFrame, visibleViewport } from '../helper/frameCapture';
import { TRACKER_FRAME_WIDTH } from '../helper/liveConstants';
import { expandRelBox } from '../helper/overlayGeometry';
import {
    KeypointConsensus,
    TrackerAlgorithm,
    TrackerKeypoint,
    clampAdoptedBoxSize,
    selectKeypoints,
    selectReplenishKeypoints,
    trackKeypoints,
} from '../helper/trackerMath';

const PATCH_SIZE = 16;
const SEARCH_RADIUS = 8;
const FPS_WINDOW = 30;
// Default number of corner keypoints seeded for a fresh detection box (overridable per hook
// options for the debug-mode experiments). Tracking survives down to 3 consensus points, so this
// leaves plenty of headroom for occlusion/outlier kills between server re-anchors (~1/s).
const DEFAULT_KEYPOINT_COUNT = 24;
// Minimum Shi-Tomasi corner response to accept a candidate — filters flat, textureless spots
// whose matches would be ambiguous anyway. Empirically ~an order of magnitude above the response
// of sensor noise on a flat surface.
const KEYPOINT_MIN_SCORE = 2000;
// Fewer surviving seed points than this means the seeding region is too textureless to track.
const MIN_SEED_KEYPOINTS = 4;
// Keypoints are seeded in this multiple of the detection box, not just inside it: the camera
// moves, the scene does not, so the immediate surroundings (wall, mounting board) track the
// meter's on-screen position just as well and offer far more texture — crucial while the meter
// is still small in the frame. See expandRelBox for why this is bounded rather than whole-frame.
const SEED_REGION_EXPAND_FACTOR = 2.5;
// When an adopted server box lands further than this fraction of the box size away from the
// previously tracked box, the keypoints are re-seeded around the new position (the detection
// likely jumped to different content); below it the existing points keep tracking.
const RESEED_CENTER_DISTANCE_FACTOR = 0.5;
// Per-tick camera motion is logged this long, so a server response (typically 1-2s stale) can be
// motion-compensated before adoption.
const MOTION_LOG_MAX_AGE_MS = 5000;
// A gap this long between two ticks means video frames stopped arriving (dark scene forcing a
// long exposure, paused stream, backgrounded tab) — not that processing is slow. The FPS window
// is reset instead of averaging the gap in, which would trip the degrade watchdog on the first
// frame after the camera recovers.
const FRAME_GAP_RESET_MS = 1000;
// One low-FPS measurement is a strike, not a shutdown: tracking pauses this long (server boxes
// keep driving the overlay) and then retries with a fresh measurement window. Low FPS is usually
// environmental (low-light camera throttling), which recovers by itself.
const DEGRADE_RETRY_COOLDOWN_MS = 3000;
// Strikes further apart than this are forgiven — only rapid consecutive strikes indicate a
// device that genuinely cannot sustain the tracker (the case the degrade path was built for).
const DEGRADE_STRIKE_WINDOW_MS = 30000;
// This many strikes within the window shut the tracker down for good.
const MAX_DEGRADE_STRIKES = 3;
// Replenish aging keypoints while approaching: matching patches were cut at the OLD scale, so
// points die in droves exactly during the final approach. Once fewer than half the budget is
// alive, dead slots are re-seeded around the CURRENT box (fresh patches at the current scale).
// Throttled — seeding costs a full corner scan.
const REPLENISH_ALIVE_RATIO = 0.5;
const REPLENISH_MIN_INTERVAL_MS = 500;
// A single detection response may return an outlier box (too wide/tall near the frame edge).
// While local tracking is alive, an adopted box may change each edge by at most this factor per
// response — real size changes converge within 2-3 responses, one outlier no longer yanks the
// overlay. Without live tracking there is nothing to compare against: adopt verbatim as before.
const ADOPT_MAX_EDGE_FACTOR = 1.3;

export interface ServerDetection {
    box: RelBox;
    // performance.now() of the frame the detection was computed from; without it the box is
    // adopted uncompensated.
    capturedAt?: number;
}

export interface TrackerDebugInfo {
    // Keypoint positions relative (0..1) to the raw video frame — same space as the AI boxes.
    keypoints: Array<{ x: number; y: number; alive: boolean }>;
    consensus: KeypointConsensus | null;
    fps: number | null;
    frameWidth: number;
    frameHeight: number;
}

interface UseBoxTrackerOptions {
    videoRef: RefObject<HTMLVideoElement>;
    serverDetection: ServerDetection | null;
    enabled: boolean;
    minFps: number;
    onDegraded?: () => void;
    debug?: boolean;
    algorithm?: TrackerAlgorithm;
    keypointCount?: number;
}

interface UseBoxTrackerResult {
    box: RelBox | null;
    mode: 'tracking' | 'snap';
    debugInfo: TrackerDebugInfo | null;
}

export const useBoxTracker = ({
    videoRef,
    serverDetection,
    enabled,
    minFps,
    onDegraded,
    debug = false,
    algorithm = 'ncc',
    keypointCount = DEFAULT_KEYPOINT_COUNT,
}: UseBoxTrackerOptions): UseBoxTrackerResult => {
    const [box, setBox] = useState<RelBox | null>(serverDetection?.box ?? null);
    const [mode, setMode] = useState<'tracking' | 'snap'>('snap');
    const [debugInfo, setDebugInfo] = useState<TrackerDebugInfo | null>(null);

    const keypointsRef = useRef<TrackerKeypoint[] | null>(null);
    const boxRef = useRef<RelBox | null>(serverDetection?.box ?? null);
    // Per-tick consensus motion (relative units), used to forward-project stale server boxes.
    const motionLogRef = useRef<Array<{ t: number; dx: number; dy: number; scale: number }>>([]);
    // performance.now() of the last keypoint replenish, throttling the corner scan.
    const lastReplenishAtRef = useRef(0);
    const tickTimesRef = useRef<number[]>([]);
    const fpsRef = useRef<number | null>(null);
    const degradedRef = useRef(false);
    const degradeStrikesRef = useRef(0);
    const lastStrikeAtRef = useRef(0);
    // While set, tracking is paused after an FPS strike; ticks pass through until this deadline.
    const pausedUntilRef = useRef<number | null>(null);
    const onDegradedRef = useRef(onDegraded);
    onDegradedRef.current = onDegraded;
    const debugRef = useRef(debug);
    debugRef.current = debug;

    // Switching the matcher or the point budget invalidates the current anchor — re-seed so the
    // comparison starts clean.
    useEffect(() => {
        keypointsRef.current = null;
    }, [algorithm, keypointCount]);

    // New server detection: the server box is ground truth and always wins — but it describes a
    // frame captured 1-2s ago, so it is first forward-projected by all camera motion the local
    // tracker has observed since then (motion log). This continuously corrects tracker drift on
    // every response without yanking the box back against the live camera movement. The keypoints
    // themselves keep running (they track the scene, independent of where the box sits) unless
    // the corrected box lands far from the previous one — then the detection likely jumped to
    // different content and the points are re-seeded around it.
    useEffect(() => {
        if (!serverDetection) {
            boxRef.current = null;
            setBox(null);
            keypointsRef.current = null;
            return;
        }

        let target = serverDetection.box;
        if (serverDetection.capturedAt != null) {
            for (const motion of motionLogRef.current) {
                if (motion.t <= serverDetection.capturedAt) {
                    continue;
                }
                const centerX = target.x + target.w / 2 + motion.dx;
                const centerY = target.y + target.h / 2 + motion.dy;
                const w = target.w * motion.scale;
                const h = target.h * motion.scale;
                target = { x: centerX - w / 2, y: centerY - h / 2, w, h };
            }
        }

        const previous = boxRef.current;
        // While local tracking is alive, guard against a single outlier detection (too wide/tall,
        // typically near the frame edge) yanking the overlay right when local tracking is
        // weakest — clamp the size jump, but let the (clamped) box still land wherever the
        // detection says. Without live tracking there is nothing trustworthy to compare against,
        // so the box is adopted verbatim, same as before.
        if (previous && keypointsRef.current && keypointsRef.current.length > 0) {
            target = clampAdoptedBoxSize(previous, target, ADOPT_MAX_EDGE_FACTOR);
        }
        boxRef.current = target;
        setBox(target);

        if (!keypointsRef.current || degradedRef.current || !previous) {
            keypointsRef.current = null;
            return;
        }
        const centerDx = target.x + target.w / 2 - (previous.x + previous.w / 2);
        const centerDy = target.y + target.h / 2 - (previous.y + previous.h / 2);
        const threshold = Math.max(target.w, target.h) * RESEED_CENTER_DISTANCE_FACTOR;
        if (Math.hypot(centerDx, centerDy) > threshold) {
            keypointsRef.current = null;
        }
    }, [serverDetection]);

    useEffect(() => {
        if (!enabled || degradedRef.current) {
            return undefined;
        }

        let cancelled = false;
        let handle = 0;
        const video = videoRef.current as (HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }) | null;
        const useRvfc = !!video?.requestVideoFrameCallback;

        // A low-FPS measurement: usually environmental (low-light camera throttling), so the
        // first strikes only pause tracking for a cooldown and retry. Only rapid consecutive
        // strikes — a device that cannot sustain the tracker at all — shut it down permanently.
        const degrade = (now: number) => {
            if (degradedRef.current) {
                return;
            }
            tickTimesRef.current = [];
            if (now - lastStrikeAtRef.current > DEGRADE_STRIKE_WINDOW_MS) {
                degradeStrikesRef.current = 0;
            }
            degradeStrikesRef.current += 1;
            lastStrikeAtRef.current = now;
            setMode('snap');
            setBox(boxRef.current);
            if (degradeStrikesRef.current >= MAX_DEGRADE_STRIKES) {
                degradedRef.current = true;
                onDegradedRef.current?.();
                return;
            }
            keypointsRef.current = null;
            pausedUntilRef.current = now + DEGRADE_RETRY_COOLDOWN_MS;
        };

        const publishDebug = (frame: { width: number; height: number }, keypoints: TrackerKeypoint[] | null, consensus: KeypointConsensus | null) => {
            if (!debugRef.current) {
                return;
            }
            setDebugInfo({
                keypoints: (keypoints ?? []).map((kp) => ({ x: kp.x / frame.width, y: kp.y / frame.height, alive: kp.alive })),
                consensus,
                fps: fpsRef.current,
                frameWidth: frame.width,
                frameHeight: frame.height,
            });
        };

        const tick = (now: number) => {
            if (cancelled || degradedRef.current) {
                return;
            }
            schedule();

            if (pausedUntilRef.current != null) {
                if (now < pausedUntilRef.current) {
                    return;
                }
                pausedUntilRef.current = null;
            }

            const times = tickTimesRef.current;
            // A long gap means frames stopped coming, not slow processing — measure afresh.
            if (times.length > 0 && now - times[times.length - 1] > FRAME_GAP_RESET_MS) {
                times.length = 0;
            }
            times.push(now);
            if (times.length > FPS_WINDOW) {
                times.shift();
            }
            if (times.length === FPS_WINDOW) {
                const fps = ((times.length - 1) / (times[times.length - 1] - times[0])) * 1000;
                fpsRef.current = Math.round(fps);
                (window as any).__aiReadingTrackerFps = fpsRef.current;
                if (fps < minFps) {
                    degrade(now);
                    return;
                }
            }

            const currentBox = boxRef.current;
            const currentVideo = videoRef.current;
            if (!currentBox || !currentVideo) {
                return;
            }

            // Cropped to the visible viewport — the same coordinate space as the AI boxes and
            // the on-screen overlay.
            const frame = grayscaleFrame(currentVideo, TRACKER_FRAME_WIDTH, visibleViewport());
            if (!frame) {
                return;
            }

            // (Re-)seed keypoints in the expanded neighborhood of the current box; if even that
            // is too textureless (e.g. meter far away on a bare wall), fall back to the whole
            // frame. Still too few corners → stay in snap mode (server boxes keep driving the
            // overlay) and retry next frame — a content property, not a device problem, so no
            // degrade.
            if (!keypointsRef.current) {
                const expanded = expandRelBox(currentBox, SEED_REGION_EXPAND_FACTOR);
                const region = {
                    x: expanded.x * frame.width,
                    y: expanded.y * frame.height,
                    w: expanded.w * frame.width,
                    h: expanded.h * frame.height,
                };
                const seedOptions = { count: keypointCount, patchSize: PATCH_SIZE, minScore: KEYPOINT_MIN_SCORE };
                let seeded = selectKeypoints(frame, region, seedOptions);
                if (seeded.length < MIN_SEED_KEYPOINTS) {
                    seeded = selectKeypoints(frame, { x: 0, y: 0, w: frame.width, h: frame.height }, seedOptions);
                }
                if (seeded.length < MIN_SEED_KEYPOINTS) {
                    setMode('snap');
                    publishDebug(frame, seeded, null);
                    return;
                }
                keypointsRef.current = seeded;
                setMode('tracking');
                // The freshly seeded points can be matched right away (zero offset on the same
                // frame) — no tick is lost.
            }

            const consensus = trackKeypoints(frame, keypointsRef.current, PATCH_SIZE, SEARCH_RADIUS, algorithm);
            if (!consensus) {
                // Target lost (occlusion, fast pan, left the frame) — drop back to snap mode and
                // wait for the next server detection to re-anchor.
                publishDebug(frame, keypointsRef.current, null);
                keypointsRef.current = null;
                setMode('snap');
                return;
            }

            // Log this tick's motion (relative units) for forward-projecting stale server boxes.
            const motionLog = motionLogRef.current;
            motionLog.push({ t: now, dx: consensus.dx / frame.width, dy: consensus.dy / frame.height, scale: consensus.scale });
            while (motionLog.length > 0 && motionLog[0].t < now - MOTION_LOG_MAX_AGE_MS) {
                motionLog.shift();
            }

            // Replenish dead keypoint slots once too many have died — their NCC patches were cut
            // at the OLD scale, so points die in droves during the final approach (the matcher is
            // not scale-invariant). Re-seeding around the CURRENT box gives fresh, current-scale
            // patches. This does not touch this tick's consensus (already computed above); the
            // new points start contributing from the next tick. Throttled — seeding costs a full
            // corner scan.
            if (consensus.alive < keypointCount * REPLENISH_ALIVE_RATIO && now - lastReplenishAtRef.current > REPLENISH_MIN_INTERVAL_MS) {
                const alive = keypointsRef.current.filter((kp) => kp.alive);
                const deadSlots = keypointCount - alive.length;
                if (deadSlots > 0) {
                    const expanded = expandRelBox(currentBox, SEED_REGION_EXPAND_FACTOR);
                    const region = {
                        x: expanded.x * frame.width,
                        y: expanded.y * frame.height,
                        w: expanded.w * frame.width,
                        h: expanded.h * frame.height,
                    };
                    const fresh = selectReplenishKeypoints(frame, region, keypointsRef.current, {
                        count: deadSlots,
                        patchSize: PATCH_SIZE,
                        minScore: KEYPOINT_MIN_SCORE,
                        minDistance: PATCH_SIZE,
                    });
                    // Fresh points can be matched right away (zero offset on the same frame) —
                    // same reasoning as full seeding above.
                    keypointsRef.current = [...alive, ...fresh];
                }
                lastReplenishAtRef.current = now;
            }

            const centerX = currentBox.x + currentBox.w / 2 + consensus.dx / frame.width;
            const centerY = currentBox.y + currentBox.h / 2 + consensus.dy / frame.height;
            const width = currentBox.w * consensus.scale;
            const height = currentBox.h * consensus.scale;
            const moved: RelBox = { x: centerX - width / 2, y: centerY - height / 2, w: width, h: height };
            boxRef.current = moved;
            setBox(moved);
            publishDebug(frame, keypointsRef.current, consensus);
        };

        const schedule = () => {
            if (cancelled || degradedRef.current) {
                return;
            }
            if (useRvfc && videoRef.current) {
                handle = (videoRef.current as any).requestVideoFrameCallback(() => tick(performance.now()));
            } else {
                handle = window.requestAnimationFrame((t) => tick(t));
            }
        };

        schedule();

        return () => {
            cancelled = true;
            if (useRvfc) {
                (videoRef.current as any)?.cancelVideoFrameCallback?.(handle);
            } else {
                window.cancelAnimationFrame(handle);
            }
        };
    }, [enabled, minFps, videoRef, algorithm, keypointCount]);

    return { box, mode, debugInfo };
};
